import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { BookOpen, Check } from 'lucide-react'
import { useState } from 'react'
import { EmptyState } from '@/components/app/empty-state'
import { ListSkeleton } from '@/components/app/list-skeleton'
import { Screen } from '@/components/app/screen'
import { ScreenHeader } from '@/components/app/screen-header'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { SERVICE_TYPE_LABELS } from '@/lib/constants'
import { formatDateFR } from '@/lib/format'
import { type MemberService, memberServicesQuery } from '@/lib/queries/services'

// Le chemin ne peut pas être `/cultes` : l'espace staff l'occupe déjà, et les
// deux layouts sont sans segment d'URL. `/mes-cultes` suit `/mes-presences`.
export const Route = createFileRoute('/_fidele/mes-cultes/')({
  component: MyServicesScreen,
})

function serviceDateLong(date: string) {
  return formatDateFR(date, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function MyServicesScreen() {
  const { data, isPending, isError } = useQuery(memberServicesQuery())
  const [selected, setSelected] = useState<MemberService | null>(null)

  const total = data?.meta.total ?? 0

  return (
    <Screen withTabBar>
      <ScreenHeader
        title="Cultes"
        subtitle={total > 0 ? `${total} culte${total > 1 ? 's' : ''} de votre église` : undefined}
      />

      <div className="px-5">
        {isPending && <ListSkeleton rows={5} />}

        {isError && (
          <EmptyState message="Impossible de charger les cultes. Vérifiez votre connexion." />
        )}

        {data && data.data.length === 0 && <EmptyState message="Aucun culte enregistré." />}

        {data && data.data.length > 0 && (
          <ul className="flex flex-col gap-2.5">
            {data.data.map((service) => (
              <li key={service.id}>
                <button
                  type="button"
                  onClick={() => setSelected(service)}
                  className="flex w-full items-start gap-3 rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-3.5 text-left active:border-gold"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold text-chalk">
                      {SERVICE_TYPE_LABELS[service.type] ?? service.type}
                    </p>
                    {service.theme && (
                      <p className="truncate text-[13px] text-gold">{service.theme}</p>
                    )}
                    <p className="mt-0.5 text-[12px] text-steel">{serviceDateLong(service.date)}</p>
                  </div>

                  {/* Marque la présence sans jamais masquer un culte manqué :
                      c'est précisément celui-là qu'on vient rattraper. */}
                  {service.attended && (
                    <span
                      className="mt-0.5 flex items-center gap-1 rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-semibold text-gold"
                      title="Vous étiez présent"
                    >
                      <Check size={12} aria-hidden="true" />
                      Présent
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ServiceDetailSheet service={selected} onClose={() => setSelected(null)} />
    </Screen>
  )
}

function ServiceDetailSheet({
  service,
  onClose,
}: {
  service: MemberService | null
  onClose: () => void
}) {
  return (
    <Sheet open={service !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="max-h-[85dvh] border-navy-800 bg-navy-900">
        {service && (
          <>
            <SheetHeader>
              <SheetTitle className="text-chalk">
                {SERVICE_TYPE_LABELS[service.type] ?? service.type}
              </SheetTitle>
            </SheetHeader>

            <div className="flex flex-col gap-5 overflow-y-auto px-5 pb-8">
              <div>
                <p className="text-[13px] text-steel">{serviceDateLong(service.date)}</p>
                {service.theme && (
                  <p className="mt-1 text-[18px] font-semibold leading-snug text-gold">
                    {service.theme}
                  </p>
                )}
                {service.preacher && (
                  <p className="mt-1 text-[13px] text-steel">Par {service.preacher}</p>
                )}
              </div>

              {service.verses && (
                <div className="flex items-start gap-3 rounded-[14px] border border-navy-800 bg-navy-950 px-4 py-3">
                  <BookOpen size={16} color="#C9A84C" className="mt-0.5 shrink-0" />
                  <p className="text-[15px] leading-relaxed text-chalk">{service.verses}</p>
                </div>
              )}

              {service.summary ? (
                <div>
                  <p className="mb-2 text-[11px] font-semibold tracking-[0.8px] text-steel uppercase">
                    Résumé
                  </p>
                  {/* `whitespace-pre-line` : le résumé est saisi en texte libre,
                      ses retours à la ligne portent le découpage du message. */}
                  <p className="whitespace-pre-line text-[15px] leading-relaxed text-chalk">
                    {service.summary}
                  </p>
                </div>
              ) : (
                <p className="text-[14px] text-steel">
                  Le résumé de ce culte n'a pas encore été publié.
                </p>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
